export enum CandidateStatus {
  Submitted = 'Submitted',
  CallRequested = 'Interview requested',
  CallCancelled = 'Interview cancelled',
  CallScheduled = 'Interview scheduled',
  InterviewDone = 'Interview done',
  CallPassed = 'Interview passed',
  CallFailed = 'Call Failed',
  Rejected = 'Rejected',
  HiringApproved = 'Approved for hiring',
  Hired = 'Hired',
  CandidateNoLongerAvailable = 'Candidate no longer available',
}

export enum ClientFeedbackResolution {
  CallRequested = 'Interview requested',
  Rejected = 'Rejected',
  HiringApproved = 'Approved for hiring',
}

export enum CandidateDetailsAction {
  APPROVE_CANDIDATE = 'approve_candidate',
  SCHEDULE_INTERVIEW = 'schedule_interview',
  SCHEDULE_NEXT_ROUND = 'schedule_next_round',
  SKIP_CANDIDATE = 'skip_candidate',
  RESCHEDULE_INTERVIEW = 'reschedule_interview',
  CANCEL_INTERVIEW = 'cancel_interview',
  CLIENT_INTERVIEW_PASSED = 'client_interview_passed',
  CLIENT_INTERVIEW_FAILED = 'client_interview_failed',
  ASK_QUESTION = 'ask_question',
}

export const CandidateStatusOrder = [
  CandidateStatus.Submitted,
  CandidateStatus.CallRequested,
  CandidateStatus.CallCancelled,
  CandidateStatus.CallScheduled,
  CandidateStatus.InterviewDone,
  CandidateStatus.CallPassed,
  CandidateStatus.HiringApproved,
  CandidateStatus.Hired,
  CandidateStatus.CandidateNoLongerAvailable,
  CandidateStatus.CallFailed,
  CandidateStatus.Rejected,
];

export const CandidateStatusPriorityOrder = [
  CandidateStatus.Submitted,
  CandidateStatus.CallPassed,
  CandidateStatus.InterviewDone,
  CandidateStatus.CallScheduled,
  CandidateStatus.CallRequested,
  CandidateStatus.HiringApproved,
  CandidateStatus.Hired,
  CandidateStatus.Rejected,
  CandidateStatus.CallFailed,
  CandidateStatus.CandidateNoLongerAvailable,
  CandidateStatus.CallCancelled,
];

export const CandidateListingStatusLabel: Record<CandidateStatus, string> = {
  [CandidateStatus.Submitted]: 'Waiting for review',
  [CandidateStatus.CallRequested]: 'Setting up interview',
  [CandidateStatus.CallCancelled]: 'Interview cancelled',
  [CandidateStatus.CallScheduled]: 'Interview scheduled',
  [CandidateStatus.InterviewDone]: 'Interview done',
  [CandidateStatus.CallPassed]: 'Interview passed',
  [CandidateStatus.CallFailed]: 'Interview failed',
  [CandidateStatus.Rejected]: 'Rejected',
  [CandidateStatus.HiringApproved]: 'Approved for hiring',
  [CandidateStatus.Hired]: 'Hired',
  [CandidateStatus.CandidateNoLongerAvailable]: 'Candidate no longer available',
};

export const CandidateStageLabel = {
  review: 'Waiting for review',
  setup: 'Set up interviews',
  pending: 'Pending interview',
  feedback: 'Waiting for feedback',
  decision: 'Hiring decision',
} as const;

export type CandidateStageKey = keyof typeof CandidateStageLabel;

export const CandidateStageByStatus: Partial<
  Record<CandidateStatus, CandidateStageKey>
> = {
  [CandidateStatus.Submitted]: 'review',
  [CandidateStatus.CallRequested]: 'setup',
  [CandidateStatus.CallCancelled]: 'setup',
  [CandidateStatus.CallScheduled]: 'pending',
  [CandidateStatus.InterviewDone]: 'feedback',
  [CandidateStatus.CallPassed]: 'decision',
};

export const CandidateStatusMeaning: Record<CandidateStatus, string> = {
  [CandidateStatus.Submitted]: 'Waiting for you to review this profile',
  [CandidateStatus.CallRequested]:
    'Waiting for AgileEngine to confirm a time with the candidate',
  [CandidateStatus.CallCancelled]:
    'The interview was called off. Rebooking puts the candidate back in the pipeline',
  [CandidateStatus.CallScheduled]:
    'A time is booked. Nothing is needed from you until it happens',
  [CandidateStatus.InterviewDone]:
    'The interview has happened and is waiting on your decision',
  [CandidateStatus.CallPassed]:
    'AgileEngine passed this candidate at their own interview',
  [CandidateStatus.CallFailed]: 'Closed after the interview',
  [CandidateStatus.Rejected]:
    'Closed. This candidate is no longer in the running',
  [CandidateStatus.HiringApproved]:
    'You approved this candidate. Expect confirmation from the AgileEngine team',
  [CandidateStatus.Hired]: 'Hired, and on the team',
  [CandidateStatus.CandidateNoLongerAvailable]:
    'The candidate is no longer available and was closed for that reason',
};

export const ResolutionTextByCandidateDetailsAction: Record<
  CandidateDetailsAction,
  string
> = {
  [CandidateDetailsAction.APPROVE_CANDIDATE]: 'Approve candidate',
  [CandidateDetailsAction.SKIP_CANDIDATE]: 'Skip candidate',
  [CandidateDetailsAction.CLIENT_INTERVIEW_PASSED]: 'Passed the interview',
  [CandidateDetailsAction.CLIENT_INTERVIEW_FAILED]:
    'Failed, keep interviewing other candidates',
  [CandidateDetailsAction.SCHEDULE_INTERVIEW]: '',
  [CandidateDetailsAction.SCHEDULE_NEXT_ROUND]: '',
  [CandidateDetailsAction.ASK_QUESTION]: '',
  [CandidateDetailsAction.RESCHEDULE_INTERVIEW]: '',
  [CandidateDetailsAction.CANCEL_INTERVIEW]: '',
};

export const ResolutionPassedByCandidateDetailsAction: Partial<
  Record<CandidateDetailsAction, boolean>
> = {
  [CandidateDetailsAction.APPROVE_CANDIDATE]: true,
  [CandidateDetailsAction.SKIP_CANDIDATE]: false,
  [CandidateDetailsAction.CLIENT_INTERVIEW_PASSED]: true,
  [CandidateDetailsAction.CLIENT_INTERVIEW_FAILED]: false,
};

export enum InterviewSlotOption {
  Date = 'date',
  MyCalendar = 'myCalendar',
}

export const CANDIDATE_INTERVIEW_CANCELLED = 'CANDIDATE_INTERVIEW_CANCELLED';

export const RESCHEDULE_REASONS = [
  'Interviewer is unavailable',
  'Need another attendee',
  'Priorities shifted on our side',
];

export const ANOTHER_ROUND_REASONS = [
  'Want to go deeper on a specific skill',
  'Someone else should meet them',
  'The panel did not agree',
  'Comparing against another candidate',
  'The candidate asked to meet more of the team',
];

export const MORE_INFO_TOPICS = [
  'Depth in a specific skill',
  'More on a particular project or role',
  'Start date and notice period',
  'Bill rate',
  'English and communication level',
  'References',
  'A code sample or portfolio',
];

export const REJECTION_REASONS = [
  'Not a fit by location',
  'Not a fit by seniority',
  'Not a fit by technical skills',
  'Not a fit by communication skills',
  'Other',
];
